<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import type { Address } from "@types"
import type { CompanyType, OwnerOutput } from "@core/composables"
import type { SourceConsentSignBuilding } from "@core/types/source/consent_request"
import { SOURCES_TYPES } from "@core/constants"
import type { ConsentExtendListPatchFields } from "@core/components/consent/consent.events"

// ================================
// TYPES & INTERFACES
// ================================

/**
 * Titulaire au format proche du legacy (`ModalSourceExtendConsent` / carte compteur).
 */
export interface SourceExtendDialogMeterOwner {
    _id?: string
    firstname?: string
    lastname?: string
    username?: string
    company?: string | null
    companyType?: string | null
}

/**
 * Compteur minimal pour prolonger le consentement depuis l’app connectée.
 */
export interface SourceExtendDialogMeter {
    _id: string
    idtype: string
    idsource: string
    address?: Address | null
    owner?: SourceExtendDialogMeterOwner | null
}

type SignedPayload = {
    consentId?: string
    sourceId?: string
    token?: string
    taskOnboarding?: unknown
    consentementFields?: ConsentExtendListPatchFields
}

type ExtendInvitationPhase = "invite" | "sent"

/**
 * Raison de fermeture du dialog prolongement (événement `done`).
 */
export type SourceExtendDialogDoneKind =
    /** Signature directe par le titulaire : recharger la liste des consentements, pas les demandes. */
    | "signed_direct"
    /** Invitation envoyée : recharger les demandes de consentement, pas la liste DDA. */
    | "invitation_completed"
    /** Fermeture sans finaliser (ex. « Quitter » sur l’écran de signature). */
    | "quit"

export type SourceExtendDialogDonePayload = {
    kind: SourceExtendDialogDoneKind
}

// ================================
// PROPS & EMITS
// ================================

const props = withDefaults(
    defineProps<{
        /**
         * Compteur concerné (`_id` = identifiant Mongo de la source).
         */
        meter: SourceExtendDialogMeter
        /**
         * Identifiant Mongo du bâtiment rattaché.
         */
        buildingId: string
        /**
         * Durée affichée dans les conditions (mois), défaut aligné sur SourceConsentSign.
         */
        durationMonths?: number
        /**
         * true si l’utilisateur connecté peut prolonger en signature directe (titulaire) :
         * personne morale → même `_id_company` ; personne physique → même `_id_user` ou même e-mail (`username`).
         * false : invitation au titulaire (e-mail + lien).
         */
        isConsentHolder?: boolean
    }>(),
    {
        durationMonths: 60,
        isConsentHolder: true,
    }
)

const emit = defineEmits<{
    cancel: []
    done: [payload: SourceExtendDialogDonePayload]
    "update:consent": [payload: SignedPayload]
}>()

const visible = defineModel<boolean>("visible", { default: false })

// ================================
// I18N
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            dialog_header: "Prolongement du consentement",
        },
    },
})

// ================================
// STATE
// ================================

/** Évite d’émettre `cancel` à la fermeture programmée après succès. */
const skipVisibilityCancel = ref(false)

/** Parcours non-titulaire : formulaire d’invitation puis écran envoyé. */
const phase = ref<ExtendInvitationPhase>("invite")

const sentPayload = ref<{ _id_consentrequest: string; url: string } | null>(
    null
)

// ================================
// COMPUTED
// ================================

/**
 * Titulaire au format `OwnerOutput` pour `SourceConsentSign` / invitation.
 */
const getOwner = computed<OwnerOutput>(() => {
    const o = props.meter.owner
    const companyName = o?.company
    return {
        user: {
            _id: o?._id,
            firstname: o?.firstname ?? "",
            lastname: o?.lastname ?? "",
            username: o?.username ?? "",
        },
        company: companyName
            ? {
                  name: companyName,
                  address: null,
                  type: (o?.companyType?.trim() || "") as CompanyType | "",
              }
            : null,
    }
})

const getBuilding = computed<SourceConsentSignBuilding>(() => ({
    _id_building: props.buildingId,
    address: props.meter.address ?? {
        street: "",
        zipcode: "",
        city: "",
        country: "FR",
        citycode: undefined,
    },
}))

const getDateFrom = computed(
    () => new Date().toISOString().slice(0, 10) as string
)

const getIsPce = computed(
    () => String(props.meter.idtype).toLowerCase() === SOURCES_TYPES.PCE
)

const getIdTypeParam = computed(() => (getIsPce.value ? "pce" : "pdl"))

const getAddress = computed((): Address => {
    const a = props.meter.address
    if (a && "street" in a) {
        return {
            street: a.street ?? "",
            zipcode: a.zipcode ?? "",
            city: a.city ?? "",
            country: a.country ?? "FR",
            citycode: a.citycode ?? undefined,
        }
    }
    return {
        street: "",
        zipcode: "",
        city: "",
        country: "FR",
        citycode: undefined,
    }
})

// ================================
// WATCHERS
// ================================

watch(visible, (v) => {
    if (v && !props.isConsentHolder) {
        phase.value = "invite"
        sentPayload.value = null
    }
})

// ================================
// ACTIONS
// ================================

function onDialogVisibleUpdate(v: boolean): void {
    if (v) return
    if (skipVisibilityCancel.value) {
        skipVisibilityCancel.value = false
        return
    }
    emit("cancel")
}

function onBack(): void {
    visible.value = false
    emit("cancel")
}

/**
 * Après signature : notification parent puis fermeture immédiate du dialog.
 */
function onSigned(payload: SignedPayload): void {
    emit("update:consent", payload)
    skipVisibilityCancel.value = true
    visible.value = false
    emit("done", { kind: "signed_direct" })
}

function onClickQuit(): void {
    skipVisibilityCancel.value = true
    visible.value = false
    emit("done", { kind: "quit" })
}

function onInvitationBack(): void {
    visible.value = false
    emit("cancel")
}

function onInvitationSent(payload: {
    _id_consentrequest: string
    url: string
}): void {
    sentPayload.value = payload
    phase.value = "sent"
}

function onInvitationSentDone(): void {
    emit("update:consent", {
        consentId: sentPayload.value?._id_consentrequest,
        sourceId: props.meter._id,
    })
    skipVisibilityCancel.value = true
    visible.value = false
    sentPayload.value = null
    emit("done", { kind: "invitation_completed" })
}
</script>

<template>
    <BaseDialog
        v-model:visible="visible"
        modal
        :closable="true"
        position="top"
        :header="t('dialog_header')"
        :style="{ width: '50vw' }"
        :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
        :pt="{
            content:
                'max-h-[calc(100dvh-10rem)] overflow-y-auto overflow-x-hidden flex justify-center items-start w-full py-2',
        }"
        @update:visible="onDialogVisibleUpdate"
    >
        <SourceConsentSign
            v-if="isConsentHolder"
            class="w-full max-w-3xl shrink-0 py-2"
            :owner="getOwner"
            :source="{
                _id_source: meter._id,
                idsource: meter.idsource,
                idtype: meter.idtype,
            }"
            :building="getBuilding"
            :date-from="getDateFrom"
            :request="{
                type: 'EXTEND',
                mail: false,
                duration: durationMonths,
            }"
            hide-header-title
            @back="onBack"
            @signed="onSigned"
            @click:quit="onClickQuit"
        />
        <SourceInvitationExtend
            v-else-if="phase === 'invite'"
            class="self-center w-full max-w-3xl py-2"
            :source-mongo-id="meter._id"
            :address="getAddress"
            :idsource="meter.idsource"
            :type="getIdTypeParam"
            :owner="getOwner"
            @back="onInvitationBack"
            @sent="onInvitationSent"
        />
        <SourceInvitationExtendSent
            v-else-if="phase === 'sent' && sentPayload"
            class="w-full max-w-3xl shrink-0 py-2"
            :consent-request-id="sentPayload._id_consentrequest"
            :link="sentPayload.url"
            @done="onInvitationSentDone"
        />
    </BaseDialog>
</template>
